/**
 * ADR-180 / ADR-183 — where a visit came from, kept until it is sent with a lead.
 *
 * The record: the page that referred the visitor, any UTM tags and any ad
 * click ids. The CRM classifies the lead's traffic source from them (organic,
 * ads, social, referral, direct) and stores the click ids (ADR-181). Only the
 * browser can see the referrer, and only on the page a visit lands on, so it
 * is read when the visit arrives and kept.
 *
 * The rule is last non-direct touch, for 30 days:
 * - An arrival replaces the whole record: a URL with UTM tags or an ad click
 *   id, or another site's page as referrer. The visitor came a new way, and
 *   the old tags and ids go with the old touch.
 * - Anything else — a reload, a link within the site, a typed address, a
 *   bookmark — keeps the record. Direct never overwrites a known source; it
 *   is recorded only when nothing is known.
 * - The record expires 30 days after the arrival that set it. Direct visits
 *   in between do not extend it.
 * That is the convention analytics tools use. It makes a lead from someone who
 * clicked an ad on Monday and wrote on Wednesday an ads lead.
 *
 * Where it is kept. For the visit, in sessionStorage. Across visits, in
 * localStorage, and only once the visitor has accepted the cookie banner,
 * whose text says this is what it is for: Swedish law (LEK 9:28) treats both
 * stores like a cookie, and the banner is where we ask. localStorage rather
 * than a cookie: the sites never read a cookie on a server, and Safari keeps a
 * script-written cookie only 24 hours after an ad click, where localStorage
 * lasts until seven days without a visit.
 *
 * A form always sends `referrer`, as "" when there was none. That is how the
 * CRM tells a direct visit from a page that predates the capture (unknown).
 */

import { hasCookieConsent } from "./cookieConsent.js"

export const VISIT_STORAGE_KEY = "visit_attribution"

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_platform"]

// Google Ads, Meta, Microsoft Ads, TikTok. The CRM stores each one (ADR-181).
export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"]

export const ATTRIBUTION_WINDOW_DAYS = 30

const WINDOW_MS = ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000
const MAX_REFERRER_LENGTH = 2000
const MAX_UTM_LENGTH = 255
const MAX_CLICK_ID_LENGTH = 512

function bareHost(host) {
  return String(host || "").toLowerCase().replace(/^www\./, "")
}

/** The referrer when it is another site's page, "" otherwise. */
export function externalReferrer(referrer, currentHostname) {
  if (!referrer) return ""
  let host
  try {
    host = new URL(referrer).hostname
  } catch (err) {
    return ""
  }
  if (!host || bareHost(host) === bareHost(currentHostname)) return ""
  return String(referrer).trim().slice(0, MAX_REFERRER_LENGTH)
}

function paramsFromSearch(search, keys, maxLength) {
  const params = new URLSearchParams(search || "")
  return keys.reduce((acc, key) => {
    const value = (params.get(key) || "").trim()
    if (value) acc[key] = value.slice(0, maxLength)
    return acc
  }, {})
}

/** Ad click ids present in a query string. */
export function clickIdsFromSearch(search) {
  return paramsFromSearch(search, CLICK_ID_KEYS, MAX_CLICK_ID_LENGTH)
}

/** UTM tags present in a query string. */
export function utmFromSearch(search) {
  return paramsFromSearch(search, UTM_KEYS, MAX_UTM_LENGTH)
}

/** Only the fields a form may send, whatever storage held. */
function sanitize(value) {
  const out = {
    referrer: typeof value?.referrer === "string" ? value.referrer.slice(0, MAX_REFERRER_LENGTH) : "",
  }
  for (const key of UTM_KEYS) {
    if (typeof value?.[key] === "string" && value[key]) out[key] = value[key].slice(0, MAX_UTM_LENGTH)
  }
  for (const key of CLICK_ID_KEYS) {
    if (typeof value?.[key] === "string" && value[key]) out[key] = value[key].slice(0, MAX_CLICK_ID_LENGTH)
  }
  return out
}

/**
 * A stored record still in its window. One without a timestamp is a session
 * record written before ADR-183; it dies with its tab anyway.
 */
function isLive(record, now) {
  if (!record || typeof record.referrer !== "string") return false
  if (typeof record.touchedAt !== "number") return true
  return now - record.touchedAt <= WINDOW_MS
}

/**
 * The record to keep after this page load, from what the load saw and what
 * is stored. See the rule at the top of this file.
 */
export function resolveVisitAttribution({ referrer, hostname, search, stored, now }) {
  const external = externalReferrer(referrer, hostname)
  const utm = utmFromSearch(search)
  const clickIds = clickIdsFromSearch(search)
  if (external || Object.keys(utm).length > 0 || Object.keys(clickIds).length > 0) {
    return { referrer: external, ...utm, ...clickIds, touchedAt: now }
  }
  if (isLive(stored, now)) {
    return { ...sanitize(stored), ...(typeof stored.touchedAt === "number" ? { touchedAt: stored.touchedAt } : {}) }
  }
  return { referrer: "", touchedAt: now }
}

function readRecord(storageName) {
  try {
    const raw = window[storageName].getItem(VISIT_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (err) {
    return null
  }
}

function writeRecord(storageName, record) {
  try {
    window[storageName].setItem(VISIT_STORAGE_KEY, JSON.stringify(record))
  } catch (err) {
    // Storage blocked (some private modes): the record is still returned.
  }
}

/** Run on every full page load (gatsby-browser's onClientEntry). */
export function captureVisitAttribution(now = Date.now()) {
  if (typeof window === "undefined") return { referrer: "" }
  const consent = hasCookieConsent()
  const record = resolveVisitAttribution({
    referrer: document.referrer,
    hostname: window.location.hostname,
    search: window.location.search,
    stored: readRecord("sessionStorage") || (consent ? readRecord("localStorage") : null),
    now,
  })
  writeRecord("sessionStorage", record)
  if (consent) writeRecord("localStorage", record)
  return sanitize(record)
}

/** What a form sends with a lead: always `referrer`, plus any UTM tags and click ids. */
export function getVisitAttribution() {
  if (typeof window === "undefined") return { referrer: "" }
  const stored = readRecord("sessionStorage")
  if (isLive(stored, Date.now())) return sanitize(stored)
  return captureVisitAttribution()
}

/**
 * The visitor has just accepted the cookie banner: keep this visit's record
 * beyond the tab. Called by CookiesBanner.
 */
export function rememberVisitAttribution() {
  if (typeof window === "undefined" || !hasCookieConsent()) return
  const record = readRecord("sessionStorage")
  if (isLive(record, Date.now())) writeRecord("localStorage", record)
}
